import { useCallback, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Divider,
  Grow,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MinusIcon from '@mui/icons-material/Remove';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import ImageIcon from '@mui/icons-material/Image';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import HtmlIcon from '@mui/icons-material/Html';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LinkIcon from '@mui/icons-material/Link';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { CoverArtOption } from 'screens/editorial/asset/components/article/InsertFieldOptions/CoverArtOption';
import { HammerOption } from 'screens/editorial/asset/components/article/InsertFieldOptions/HammerOption';
import { KickerOption } from 'screens/editorial/asset/components/article/InsertFieldOptions/KickerOption';
import { SubheadlineOption } from 'screens/editorial/asset/components/article/InsertFieldOptions/SubheadlineOption';
import { InsertItemLayout } from 'screens/editorial/asset/components/article/InsertItemLayout/InsertItemLayout';
import { CustomToolbarBtnProps } from '../types/customToolbarBtnTypes';
import { useAssetConfigContext } from 'screens/editorial/asset/context/AssetConfigContextProvider';
import { hasExtension } from 'components/EditorBar/utils';
import { IconHelper } from 'components/generic/IconHelper';
import { PlainTextPaste } from 'screens/editorial/asset/components/article/InsertFieldOptions/PlainTextPaste';
import { bloxValues } from 'themes/constants';

export const WidgetSelect = ({ editor, disabled }: CustomToolbarBtnProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const { t } = useTranslation();
  const { isReadOnlyMode } = useAssetConfigContext();
  const hasBloxInlineExt = hasExtension(editor, 'bloxInline');
  const isBloxInlineDisabled = Boolean(!hasBloxInlineExt || disabled);

  const insertInlineAsset = useCallback(
    (assetType: string) => {
      editor?.chain().focus().insertBloxInline({ type: assetType }).run();
    },
    [editor],
  );

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const dividerSx: SxProps<Theme> = useMemo(
    () => ({
      fontSize: bloxValues.tinyFontSizeRem, // 12px
      color: theme?.palette?.grey?.[600],
    }),
    [theme],
  );

  // Translated UI Labels
  const uiImage = t('blox.editorial.toolbar.widgetselect.inline_assets.image');
  const uiInsertImage = t(
    'blox.editorial.toolbar.widgetselect.inline_assets.insert_type',
    { type: uiImage },
  );
  const uiVideo = t('blox.editorial.toolbar.widgetselect.inline_assets.video');
  const uiInsertVideo = t(
    'blox.editorial.toolbar.widgetselect.inline_assets.insert_type',
    { type: uiVideo },
  );
  const uiAudio = t('blox.editorial.toolbar.widgetselect.inline_assets.audio');
  const uiInsertAudio = t(
    'blox.editorial.toolbar.widgetselect.inline_assets.insert_type',
    { type: uiAudio },
  );
  const uiLink = t('blox.editorial.toolbar.widgetselect.inline_assets.link');
  const uiInsertLink = t(
    'blox.editorial.toolbar.widgetselect.inline_assets.insert_type',
    { type: uiLink },
  );
  const uiHtml = t('blox.editorial.toolbar.widgetselect.inline_assets.html');
  const uiInsertHtml = t(
    'blox.editorial.toolbar.widgetselect.inline_assets.insert_type',
    { type: uiHtml },
  );
  const uiFile = t('blox.editorial.toolbar.widgetselect.inline_assets.file');
  const uiInsertFile = t(
    'blox.editorial.toolbar.widgetselect.inline_assets.insert_type',
    { type: uiFile },
  );
  const uiHeadlines = t('blox.editorial.toolbar.widgetselect.dividers.headlines');
  const uiRelContent = t('blox.editorial.asset.related_content.label');
  const uiRelContDesc = t('blox.editorial.asset.related_content.actions.insert.label');
  const uiUtilities = t('blox.editorial.toolbar.widgetselect.dividers.utilities');

  return (
    <div
      id="widget-select"
      data-testid="widget-select"
    >
      <ButtonGroup
        variant="text"
        ref={anchorRef}
        aria-label="widget select"
      >
        <Button
          disabled={isReadOnlyMode}
          onClick={handleToggle}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          sx={{
            textTransform: 'unset',
          }}
        >
          {open ? <MinusIcon /> : <AddIcon />}
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        onClick={handleClose}
        transition
        placement="bottom-end"
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              mainAxis: true,
              boundary: 'viewport',
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 2], // Adds some spacing between button and menu
            },
          },
        ]}
        sx={{ zIndex: bloxValues.zIndexHigh }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-end' ? 'center top' : 'center bottom',
            }}
          >
            <Paper
              sx={{
                maxHeight: 'calc(100vh - 175px)', // Subtract some space for spacing
                overflow: 'auto', // Make the paper scrollable
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  sx={{
                    maxHeight: '65vh',
                    overflowY: 'auto',
                  }}
                >
                  {/* Inline assets */}
                  <InsertItemLayout
                    title={uiImage}
                    description={uiInsertImage}
                    shortcut="/img"
                    id="widget-select-inline-image"
                    key="inline-image"
                    data-testid="widget-select-inline-image"
                    action={() => insertInlineAsset('image')}
                    disabled={isBloxInlineDisabled}
                    icon={
                      <ImageIcon
                        sx={(theme) => ({
                          color: theme.blox.iconColors.image,
                        })}
                      />
                    }
                  />

                  <InsertItemLayout
                    title={uiVideo}
                    description={uiInsertVideo}
                    shortcut="/video"
                    key="inline-video"
                    id="widget-select-inline-video"
                    data-testid="widget-select-inline-video"
                    action={() => insertInlineAsset('video')}
                    disabled={isBloxInlineDisabled}
                    icon={
                      <VideocamOutlinedIcon
                        sx={(theme) => ({
                          color: theme.blox.iconColors.video,
                        })}
                      />
                    }
                  />

                  <InsertItemLayout
                    title={uiAudio}
                    description={uiInsertAudio}
                    shortcut="/audio"
                    key="inline-audio"
                    id="widget-select-inline-audio"
                    data-testid="widget-select-inline-audio"
                    action={() => insertInlineAsset('audio')}
                    disabled={isBloxInlineDisabled}
                    icon={
                      <VolumeUpIcon
                        sx={(theme) => ({
                          color: theme.blox.iconColors.audio,
                        })}
                      />
                    }
                  />

                  <InsertItemLayout
                    title={uiLink}
                    description={uiInsertLink}
                    shortcut="/link"
                    key="inline-link"
                    id="widget-select-inline-link"
                    data-testid="widget-select-inline-link"
                    action={() => insertInlineAsset('link')}
                    disabled={isBloxInlineDisabled}
                    icon={
                      <LinkIcon
                        sx={(theme) => ({
                          color: theme.blox.iconColors.alink,
                        })}
                      />
                    }
                  />

                  <InsertItemLayout
                    title={uiHtml}
                    description={uiInsertHtml}
                    shortcut="/html"
                    key="inline-html"
                    id="widget-select-inline-html"
                    data-testid="widget-select-inline-html"
                    action={() => insertInlineAsset('html')}
                    disabled={isBloxInlineDisabled}
                    icon={
                      <HtmlIcon
                        sx={(theme) => ({
                          color: theme.blox.iconColors.html,
                        })}
                      />
                    }
                  />

                  <InsertItemLayout
                    title={uiFile}
                    description={uiInsertFile}
                    shortcut="/file"
                    id="widget-select-inline-file"
                    data-testid="widget-select-inline-file"
                    key="inline-file"
                    action={() => insertInlineAsset('file')}
                    disabled={isBloxInlineDisabled}
                    icon={
                      <AttachFileOutlinedIcon
                        sx={(theme) => ({
                          color: theme.blox.iconColors.file,
                        })}
                      />
                    }
                  />

                  {/* Design options */}
                  <Divider
                    textAlign="left"
                    sx={dividerSx}
                  >
                    {t('blox.general.design')}
                  </Divider>

                  <CoverArtOption onSelect={() => setOpen(false)} />

                  {/* Optional inputs */}
                  <Divider
                    textAlign="left"
                    sx={dividerSx}
                  >
                    {uiHeadlines}
                  </Divider>

                  <HammerOption />
                  <KickerOption />
                  <SubheadlineOption />

                  {/* Related Content */}
                  <Divider
                    textAlign="left"
                    sx={dividerSx}
                  >
                    {uiRelContent}
                  </Divider>

                  <InsertItemLayout
                    id="widget-select-relcontent"
                    key="widget-select-relcontent"
                    action={() => insertInlineAsset('relcontent')}
                    disabled={disabled || isBloxInlineDisabled}
                    title={uiRelContent}
                    icon={<IconHelper iconType="relcontent" />}
                    description={uiRelContDesc}
                  />

                  {/* Utilities */}
                  <Divider
                    textAlign="left"
                    sx={dividerSx}
                  >
                    {uiUtilities}
                  </Divider>

                  <PlainTextPaste
                    editor={editor}
                    disabled={disabled}
                  />
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
